/**
 * =====================================================================================
 * MÓDULO VUEX DE EMISOR - emisor.js
 * =====================================================================================
 *
 * Este módulo gestiona los EMISORES (empresas) y ESTABLECIMIENTOS (sucursales).
 *
 * ¿QUÉ ES UN EMISOR?
 * ------------------
 * El EMISOR es la empresa o persona que EMITE las facturas.
 * Es quien vende productos/servicios y genera documentos tributarios.
 *
 * DATOS DEL EMISOR:
 * - NIT (Número de Identificación Tributaria)
 * - NRC (Número de Registro de Contribuyente)
 * - Nombre comercial
 * - Razón social
 * - Actividad económica
 * - Dirección fiscal
 *
 * ¿QUÉ ES UN ESTABLECIMIENTO?
 * --------------------------
 * Un establecimiento (también llamado sucursal o punto de venta) es un
 * lugar FÍSICO desde donde el emisor opera y emite facturas.
 *
 * RELACIÓN EMISOR ↔ ESTABLECIMIENTOS:
 *
 *   EMISOR (Empresa ABC S.A. de C.V.)
 *      │
 *      ├── ESTABLECIMIENTO 1: Casa Matriz
 *      │      Código: M001P001
 *      │      Dirección: Calle Principal #123
 *      │
 *      ├── ESTABLECIMIENTO 2: Sucursal Norte
 *      │      Código: S001P001
 *      │      Dirección: Av. Norte #456
 *      │
 *      └── ESTABLECIMIENTO 3: Sucursal Sur
 *             Código: S002P001
 *             Dirección: Bvld. Sur #789
 *
 * ¿POR QUÉ ES IMPORTANTE?
 * ----------------------
 * 1. Cada factura tiene un NÚMERO DE CONTROL que incluye el código de establecimiento:
 *    DTE-01-[M001P001]-000000000000001
 *            ↑ código del establecimiento
 *
 * 2. El MH requiere saber desde qué punto de venta se emite cada factura
 *
 * 3. Permite reportes y control por ubicación
 *
 * PERSISTENCIA EN localStorage:
 * ----------------------------
 * Este módulo guarda el emisor y establecimiento seleccionados en localStorage
 * para que persistan entre sesiones:
 * - dte_emisor_id: ID del emisor seleccionado
 * - dte_establecimiento_id: ID del establecimiento seleccionado
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - src/services/emisores.service.js: CRUD de emisores
 * - src/services/sucursales.service.js: CRUD de sucursales/establecimientos
 * - src/views/configuracion/EmisorView.vue: Configuración del emisor
 * - src/views/configuracion/SucursalesView.vue: Gestión de sucursales
 * - src/components/layout/AppHeader.vue: Muestra emisor/establecimiento actual
 * - src/services/facturas.service.js: Usa establecimiento para generar número de control
 *
 * =====================================================================================
 */

// Importamos los servicios que hacen las peticiones HTTP
import emisoresService from '@/services/emisores.service'
import sucursalesService from '@/services/sucursales.service'

/**
 * =====================================================================================
 * STATE (Estado del Módulo)
 * =====================================================================================
 *
 * Contiene los datos del emisor y establecimientos.
 *
 * ESTRUCTURA DEL EMISOR:
 * {
 *   id: 1,
 *   nit: "0614-010101-101-0",
 *   nrc: "123456-7",
 *   nombreComercial: "Mi Empresa",
 *   nombreRazonSocial: "Mi Empresa S.A. de C.V.",
 *   actividadEconomica: { codigo: "46201", valor: "Venta al por mayor..." },
 *   direccion: { ... },
 *   telefono: "2222-3333",
 *   correo: "empresa@email.com"
 * }
 *
 * ESTRUCTURA DEL ESTABLECIMIENTO:
 * {
 *   id: 1,
 *   emisorId: 1,
 *   nombre: "Casa Matriz",
 *   codigo: "M001P001",
 *   codigoPuntoVenta: "001",
 *   codigoMH: "0001",
 *   direccion: "...",
 *   telefono: "...",
 *   activo: true
 * }
 */
const state = {
  // =================================================================================
  // EMISORES (Empresas)
  // =================================================================================
  /**
   * Lista de todos los emisores disponibles
   * En la mayoría de casos será solo uno (la empresa del usuario)
   */
  emisores: [],

  /**
   * Emisor actualmente seleccionado
   * Sus datos se usan en el encabezado de las facturas
   */
  emisorActual: null,

  // =================================================================================
  // ESTABLECIMIENTOS (Sucursales)
  // =================================================================================
  /**
   * Lista de todos los establecimientos
   */
  establecimientos: [],

  /**
   * Establecimiento actualmente seleccionado
   * Su código se usa en el número de control de las facturas
   */
  establecimientoActual: null,

  /**
   * Establecimientos a los que el usuario actual tiene acceso
   * Un usuario puede tener acceso solo a algunas sucursales
   */
  establecimientosUsuario: [],

  // =================================================================================
  // FLAGS DE CONTROL
  // =================================================================================
  /**
   * Indica si hay una operación en progreso
   */
  loading: false,

  /**
   * Mensaje de error si algo falla
   */
  error: null,

  /**
   * Indica si el selector de sucursal debe estar bloqueado
   * Se usa en el proceso de emisión de facturas para evitar cambios después del paso 1
   * Para Cajero siempre está bloqueado
   */
  selectorSucursalBloqueado: false
}

/**
 * =====================================================================================
 * GETTERS (Propiedades Computadas)
 * =====================================================================================
 *
 * Proporcionan acceso fácil a los datos y cálculos derivados.
 */
const getters = {
  // =================================================================================
  // GETTERS DE EMISORES
  // =================================================================================
  /**
   * Retorna todos los emisores
   * @returns {Array} Lista de emisores
   */
  allEmisores: state => state.emisores,

  /**
   * Retorna el emisor actualmente seleccionado
   * @returns {Object|null} Datos del emisor o null
   */
  emisorActual: state => state.emisorActual,

  /**
   * Retorna el nombre del emisor para mostrar en la UI
   * Prioriza nombre comercial sobre razón social
   *
   * OPERADOR || (OR lógico):
   * a || b → si 'a' es truthy, retorna 'a'; si no, retorna 'b'
   *
   * OPTIONAL CHAINING (?.)
   * obj?.prop → si obj es null/undefined, retorna undefined en vez de error
   *
   * @returns {string} Nombre del emisor
   */
  emisorNombre: state => state.emisorActual?.nombreComercial || state.emisorActual?.nombreRazonSocial || 'Sin seleccionar',

  /**
   * Retorna el NRC del emisor (número de registro de contribuyente)
   * @returns {string} NRC
   */
  emisorNRC: state => state.emisorActual?.nrc || '',

  /**
   * Retorna el NIT del emisor
   * @returns {string} NIT
   */
  emisorNIT: state => state.emisorActual?.nit || '',

  /**
   * Indica si hay un emisor seleccionado
   * !! convierte a booleano: !!null = false, !!{...} = true
   * @returns {boolean} true si hay emisor seleccionado
   */
  isEmisorSelected: state => !!state.emisorActual,

  // =================================================================================
  // GETTERS DE ESTABLECIMIENTOS
  // =================================================================================
  /**
   * Retorna todos los establecimientos
   */
  allEstablecimientos: state => state.establecimientos,

  /**
   * Retorna el establecimiento actualmente seleccionado
   */
  establecimientoActual: state => state.establecimientoActual,

  /**
   * Retorna los establecimientos a los que el usuario tiene acceso
   */
  establecimientosUsuario: state => state.establecimientosUsuario,

  /**
   * Retorna los establecimientos del emisor actual (solo activos)
   * El backend ya filtra por emisor del token, así que solo filtramos por activo
   *
   * @returns {Array} Establecimientos del emisor actual
   */
  establecimientosDelEmisor: state => {
    // Si no hay emisor, retornar array vacío
    if (!state.emisorActual) return []

    const emisorIdActual = state.emisorActual.id

    // Filtrar solo por activos y que pertenezcan al emisor actual
    // Usamos conversión a String para evitar problemas de tipo (number vs string)
    return state.establecimientos.filter(e => {
      const esActivo = e.activo !== false
      // Si no tiene emisorId, lo incluimos (asumimos que pertenece al emisor actual)
      // Si tiene emisorId, verificamos que coincida
      const perteneceAlEmisor = !e.emisorId || String(e.emisorId) === String(emisorIdActual)
      return esActivo && perteneceAlEmisor
    })
  },

  /**
   * Retorna el nombre del establecimiento actual
   */
  establecimientoNombre: state => state.establecimientoActual?.nombre || 'Sin seleccionar',

  /**
   * Indica si hay establecimiento seleccionado
   */
  isEstablecimientoSelected: state => !!state.establecimientoActual,

  // =================================================================================
  // GETTERS COMPUESTOS
  // =================================================================================
  /**
   * Retorna los datos completos necesarios para emitir un DTE
   * Combina emisor y establecimiento en un solo objeto
   *
   * USO:
   * const datos = store.getters['emisor/datosEmision']
   * if (datos) {
   *   // Usar datos.emisor y datos.establecimiento
   * }
   *
   * @returns {Object|null} { emisor, establecimiento } o null
   */
  datosEmision: state => {
    // Ambos son necesarios para emitir
    if (!state.emisorActual || !state.establecimientoActual) return null
    return {
      emisor: state.emisorActual,
      establecimiento: state.establecimientoActual
    }
  },

  /**
   * Estado de carga
   */
  loading: state => state.loading,

  /**
   * Mensaje de error
   */
  error: state => state.error,

  /**
   * Indica si el selector de sucursal está bloqueado
   */
  selectorSucursalBloqueado: state => state.selectorSucursalBloqueado
}

/**
 * =====================================================================================
 * MUTATIONS (Modificadores del Estado)
 * =====================================================================================
 *
 * Funciones síncronas que modifican el state directamente.
 */
const mutations = {
  // =================================================================================
  // MUTATIONS DE EMISORES
  // =================================================================================
  /**
   * Establece la lista de emisores
   */
  SET_EMISORES(state, emisores) {
    state.emisores = emisores
  },

  /**
   * Establece el emisor actual
   */
  SET_EMISOR_ACTUAL(state, emisor) {
    state.emisorActual = emisor
  },

  // =================================================================================
  // MUTATIONS DE ESTABLECIMIENTOS
  // =================================================================================
  /**
   * Establece la lista de establecimientos
   */
  SET_ESTABLECIMIENTOS(state, establecimientos) {
    state.establecimientos = establecimientos
  },

  /**
   * Establece el establecimiento actual
   */
  SET_ESTABLECIMIENTO_ACTUAL(state, establecimiento) {
    state.establecimientoActual = establecimiento
  },

  /**
   * Establece los establecimientos del usuario
   */
  SET_ESTABLECIMIENTOS_USUARIO(state, establecimientos) {
    state.establecimientosUsuario = establecimientos
  },

  // =================================================================================
  // MUTATIONS GENERALES
  // =================================================================================
  /**
   * Establece el estado de carga
   */
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  /**
   * Establece el mensaje de error
   */
  SET_ERROR(state, error) {
    state.error = error
  },

  /**
   * Limpia toda la información del módulo
   * Se usa al cerrar sesión
   */
  CLEAR_ALL(state) {
    state.emisorActual = null
    state.establecimientoActual = null
    state.establecimientosUsuario = []
    state.selectorSucursalBloqueado = false
  },

  /**
   * Establece el estado de bloqueo del selector de sucursal
   * @param {boolean} bloqueado - true para bloquear, false para desbloquear
   */
  SET_SELECTOR_SUCURSAL_BLOQUEADO(state, bloqueado) {
    state.selectorSucursalBloqueado = bloqueado
  }
}

/**
 * =====================================================================================
 * ACTIONS (Acciones Asíncronas)
 * =====================================================================================
 *
 * Funciones que pueden ser asíncronas y que llaman mutations.
 */
const actions = {
  /**
   * ===========================================================================
   * ACTION: fetchEmisores
   * ===========================================================================
   *
   * Carga los emisores desde el backend según el rol del usuario:
   * - SuperAdmin: Usa GET /api/emisores (todos los emisores)
   * - Otros roles: Usa GET /api/emisores/mi-perfil (solo su emisor)
   *
   * @param {Object} context - { commit, rootState }
   */
  async fetchEmisores({ commit, rootState }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      // Obtener el rol del usuario actual desde el store de auth
      const userRol = rootState.auth?.user?.rol

      // Obtener catálogos para resolver descripciones de departamento/municipio
      const departamentos = rootState.catalogos?.departamentos || []
      const municipios = rootState.catalogos?.municipios || []

      // Función auxiliar para obtener descripción del departamento por ID
      const getDepartamentoDesc = (catDepartamentoId) => {
        if (!catDepartamentoId) return ''
        const dept = departamentos.find(d => d.id === catDepartamentoId)
        return dept?.valor || ''
      }

      // Función auxiliar para obtener descripción del municipio por ID
      const getMunicipioDesc = (catMunicipioId) => {
        if (!catMunicipioId) return ''
        const muni = municipios.find(m => m.id === catMunicipioId)
        return muni?.valor || ''
      }

      let emisores = []

      if (userRol === 'SuperAdmin') {
        // SuperAdmin: obtener todos los emisores
        const response = await emisoresService.getAll(1, 100)
        // La respuesta es paginada: { items: [...], totalCount, pageNumber, pageSize }
        // Verificar la estructura de la respuesta
        let items = []
        if (Array.isArray(response)) {
          items = response
        } else if (response && Array.isArray(response.items)) {
          items = response.items
        } else if (response && typeof response === 'object') {
          // Si es un solo objeto, convertirlo a array
          items = [response]
        }
        emisores = items.map(emisor => ({
          id: emisor.id,
          nit: emisor.nit,
          nrc: emisor.nrc,
          nombreRazonSocial: emisor.nombreRazonSocial,
          nombreComercial: emisor.nombreComercial,
          // Alias para compatibilidad con componentes de UI
          nombre: emisor.nombreRazonSocial,
          correoElectronico: emisor.correoElectronico,
          correo: emisor.correoElectronico, // Alias
          telefono: emisor.telefono,
          // Dirección como objeto para compatibilidad
          direccion: typeof emisor.direccion === 'string'
            ? { complemento: emisor.direccion }
            : emisor.direccion,
          codigoActividad: emisor.codigoActividad,
          descripcionActividad: emisor.descripcionActividad,
          // Objeto actividadEconomica para componentes de preview
          actividadEconomica: {
            codigo: emisor.codigoActividad,
            descripcion: emisor.descripcionActividad
          },
          catDepartamentoId: emisor.catDepartamentoId,
          catMunicipioId: emisor.catMunicipioId,
          // Descripciones de departamento y municipio (buscar en catálogos si no vienen del backend)
          departamentoDesc: emisor.departamento?.valor || emisor.departamentoNombre || emisor.departamentoDesc || getDepartamentoDesc(emisor.catDepartamentoId),
          municipioDesc: emisor.municipio?.valor || emisor.municipioNombre || emisor.municipioDesc || getMunicipioDesc(emisor.catMunicipioId),
          // CAT-008 Distrito (DTE V2.0). El mapper original omitio este campo,
          // por lo que el modal Editar Emisor mostraba el dropdown vacio aun
          // cuando la BD tenia CatDistritoId poblado (via sync SH->SX o via
          // edicion directa en Smartix).
          catDistritoId: emisor.catDistritoId,
          distritoDesc: emisor.distrito?.valor || emisor.distritoNombre || emisor.distritoDesc || '',
          catTipoEstablecimientoId: emisor.catTipoEstablecimientoId,
          tipoEstablecimiento: emisor.catTipoEstablecimientoId, // Alias
          // Códigos de Establecimiento MH
          codEstableMH: emisor.codEstableMH,
          codEstable: emisor.codEstable,
          codPuntoVentaMH: emisor.codPuntoVentaMH,
          codPuntoVenta: emisor.codPuntoVenta,
          activo: emisor.activo,
          fechaCreacion: emisor.fechaCreacion,
          totalUsuarios: emisor.totalUsuarios,
          totalFacturas: emisor.totalFacturas,
          // Logo del emisor
          logoUrl: emisor.logoUrl,
          // Credenciales MH
          mhUsuario: emisor.mhUsuario,
          mhLlavePublica: emisor.mhLlavePublica,
          // Credenciales MH Producción
          mhUsuarioProd: emisor.mhUsuarioProd,
          mhLlavePublicaProd: emisor.mhLlavePublicaProd,
          // Gmail OAuth2
          gmailEmail: emisor.gmailEmail || null,
          gmailConectado: emisor.gmailConectado || false,
          // Delegacion de inventario a SmartInventory
          inventarioExterno: emisor.inventarioExterno || false,
          // Plan B Hub-as-Emisor: vínculo con SmartHub (controla read-only de Sección A)
          hubId: emisor.hubId
        }))
      } else {
        // Otros roles: obtener solo su emisor
        const emisor = await emisoresService.getMiPerfil()
        const emisorMapeado = {
          id: emisor.id,
          nit: emisor.nit,
          nrc: emisor.nrc,
          nombreRazonSocial: emisor.nombreRazonSocial,
          nombreComercial: emisor.nombreComercial,
          // Alias para compatibilidad con componentes de UI
          nombre: emisor.nombreRazonSocial,
          correoElectronico: emisor.correoElectronico,
          correo: emisor.correoElectronico, // Alias
          telefono: emisor.telefono,
          // Dirección como objeto para compatibilidad
          direccion: typeof emisor.direccion === 'string'
            ? { complemento: emisor.direccion }
            : emisor.direccion,
          codigoActividad: emisor.codigoActividad,
          descripcionActividad: emisor.descripcionActividad,
          // Objeto actividadEconomica para componentes de preview
          actividadEconomica: {
            codigo: emisor.codigoActividad,
            descripcion: emisor.descripcionActividad
          },
          catDepartamentoId: emisor.catDepartamentoId,
          catMunicipioId: emisor.catMunicipioId,
          // Descripciones de departamento y municipio (buscar en catálogos si no vienen del backend)
          departamentoDesc: emisor.departamento?.valor || emisor.departamentoNombre || emisor.departamentoDesc || getDepartamentoDesc(emisor.catDepartamentoId),
          municipioDesc: emisor.municipio?.valor || emisor.municipioNombre || emisor.municipioDesc || getMunicipioDesc(emisor.catMunicipioId),
          // CAT-008 Distrito (DTE V2.0). El mapper original omitio este campo,
          // por lo que el modal Editar Emisor mostraba el dropdown vacio aun
          // cuando la BD tenia CatDistritoId poblado (via sync SH->SX o via
          // edicion directa en Smartix).
          catDistritoId: emisor.catDistritoId,
          distritoDesc: emisor.distrito?.valor || emisor.distritoNombre || emisor.distritoDesc || '',
          catTipoEstablecimientoId: emisor.catTipoEstablecimientoId,
          tipoEstablecimiento: emisor.catTipoEstablecimientoId, // Alias
          // Códigos de Establecimiento MH
          codEstableMH: emisor.codEstableMH,
          codEstable: emisor.codEstable,
          codPuntoVentaMH: emisor.codPuntoVentaMH,
          codPuntoVenta: emisor.codPuntoVenta,
          activo: emisor.activo,
          fechaCreacion: emisor.fechaCreacion,
          totalUsuarios: emisor.totalUsuarios,
          totalFacturas: emisor.totalFacturas,
          // Logo del emisor
          logoUrl: emisor.logoUrl,
          // Credenciales MH
          mhUsuario: emisor.mhUsuario,
          mhLlavePublica: emisor.mhLlavePublica,
          // Credenciales MH Producción
          mhUsuarioProd: emisor.mhUsuarioProd,
          mhLlavePublicaProd: emisor.mhLlavePublicaProd,
          // Gmail OAuth2
          gmailEmail: emisor.gmailEmail || null,
          gmailConectado: emisor.gmailConectado || false,
          // Delegacion de inventario a SmartInventory
          inventarioExterno: emisor.inventarioExterno || false,
          // Plan B Hub-as-Emisor: vínculo con SmartHub (controla read-only de Sección A)
          hubId: emisor.hubId
        }
        emisores = [emisorMapeado]
      }

      // Guardar lista de emisores
      commit('SET_EMISORES', emisores)

      // Establecer el emisor actual (el primero de la lista o el del usuario)
      if (emisores.length > 0) {
        commit('SET_EMISOR_ACTUAL', emisores[0])
        localStorage.setItem('dte_emisor_id', emisores[0].id)
      }
    } catch (error) {
      console.error('Error al cargar emisor:', error)
      // Si es error 401/403, probablemente el token expiró
      if (error.response?.status === 401 || error.response?.status === 403) {
        commit('SET_ERROR', 'Sesión expirada. Por favor inicie sesión nuevamente.')
      } else {
        commit('SET_ERROR', 'No se pudo cargar el emisor. Verifique la conexión con el servidor.')
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: fetchEstablecimientos
   * ===========================================================================
   *
   * Carga todos los establecimientos desde el backend con datos completos.
   *
   * ESTRATEGIA:
   * ----------
   * 1. Primero obtiene la lista básica (SucursalListDto)
   * 2. Luego hace peticiones paralelas para obtener el detalle completo (SucursalDto)
   * 3. Esto permite mostrar campos como direccion, telefono, etc. en las tarjetas
   *
   * @param {Object} context - { commit }
   */
  async fetchEstablecimientos({ commit, rootState }) {
    commit('SET_LOADING', true)
    try {
      // 1. Obtener lista básica (filtrada por rol del usuario)
      const sucursalesBasicas = await sucursalesService.getAll()

      // 2. Obtener detalles completos en paralelo
      const detallesPromises = sucursalesBasicas.map(s =>
        sucursalesService.getById(s.id).catch(err => {
          console.warn(`No se pudo cargar detalle de sucursal ${s.id}:`, err)
          return null // Si falla, retornar null
        })
      )
      const detallesCompletos = await Promise.all(detallesPromises)

      // Obtener catálogo de tipos de establecimiento para buscar descripciones
      const tiposEstablecimiento = rootState.catalogos?.tiposEstablecimiento || []

      // 3. Mapear los datos completos al formato del frontend
      const establecimientos = detallesCompletos
        .filter(s => s !== null) // Filtrar los que fallaron
        .map(s => {
          // Buscar la descripción del tipo de establecimiento en el catálogo
          const tipoId = s.catTipoEstablecimientoId
          const tipoCatalogo = tiposEstablecimiento.find(t => t.id === tipoId)
          const tipoDesc = s.tipoEstablecimiento?.valor || s.tipoEstablecimientoNombre || tipoCatalogo?.valor || ''

          return {
            id: s.id,
            emisorId: s.emisorId,
            nombre: s.nombre,
            codigo: s.codigo || s.codigoEstablecimiento,
            codigoPuntoVenta: s.codigoPuntoVenta,
            codigoPuntoVentaMH: s.codigoPuntoVentaMH,
            codigoEstablecimientoMH: s.codigoEstablecimiento,
            codigoMH: s.codigoMH,
            direccion: s.direccion, // ← Ahora viene del DTO completo
            telefono: s.telefono,
            correo: s.correoElectronico,
            activo: s.activo !== false,
            // Campos descriptivos del backend
            // El backend puede enviar como objeto {valor} o como string directo
            tipoEstablecimiento: tipoId ? String(tipoId).padStart(2, '0') : '02',
            tipoEstablecimientoDesc: tipoDesc,
            departamentoDesc: s.departamento?.valor || s.departamentoNombre || '',
            municipioDesc: s.municipio?.valor || s.municipioNombre || '',
            catTipoEstablecimientoId: tipoId || null,
            catDepartamentoId: s.catDepartamentoId,
            catMunicipioId: s.catMunicipioId
          }
        })

      commit('SET_ESTABLECIMIENTOS', establecimientos)
    } catch (error) {
      console.error('Error al cargar establecimientos:', error)
      commit('SET_ERROR', 'No se pudieron cargar los establecimientos.')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: fetchEstablecimientosUsuario
   * ===========================================================================
   *
   * Carga los establecimientos a los que el usuario actual tiene acceso
   * con datos completos (usando peticiones paralelas al endpoint de detalle).
   *
   * ESTRATEGIA:
   * ----------
   * 1. Obtiene lista básica (SucursalListDto)
   * 2. Hace peticiones paralelas para obtener detalles completos (SucursalDto)
   * 3. Restaura la selección guardada en localStorage
   *
   * @param {Object} context - { commit, state }
   */
  async fetchEstablecimientosUsuario({ commit, state, rootState }) {
    commit('SET_LOADING', true)
    try {
      // 1. Obtener lista básica (restringida a la sucursal del usuario para roles con restricción)
      const sucursalesBasicas = await sucursalesService.getAll({ soloMiSucursal: true })

      // 2. Obtener detalles completos en paralelo
      const detallesPromises = sucursalesBasicas.map(s =>
        sucursalesService.getById(s.id).catch(err => {
          console.warn(`No se pudo cargar detalle de sucursal ${s.id}:`, err)
          return null
        })
      )
      const detallesCompletos = await Promise.all(detallesPromises)

      // Obtener catálogo de tipos de establecimiento para buscar descripciones
      const tiposEstablecimiento = rootState.catalogos?.tiposEstablecimiento || []

      // 3. Mapear datos completos al frontend
      const establecimientos = detallesCompletos
        .filter(s => s !== null)
        .map(s => {
          // Buscar la descripción del tipo de establecimiento en el catálogo
          const tipoId = s.catTipoEstablecimientoId
          const tipoCatalogo = tiposEstablecimiento.find(t => t.id === tipoId)
          const tipoDesc = s.tipoEstablecimiento?.valor || s.tipoEstablecimientoNombre || tipoCatalogo?.valor || ''

          return {
            id: s.id,
            emisorId: s.emisorId,
            nombre: s.nombre,
            codigo: s.codigo || s.codigoEstablecimiento,
            codigoPuntoVenta: s.codigoPuntoVenta,
            codigoPuntoVentaMH: s.codigoPuntoVentaMH,
            codigoEstablecimientoMH: s.codigoEstablecimiento,
            codigoMH: s.codigoMH,
            direccion: s.direccion, // ← Ahora viene del DTO completo
            telefono: s.telefono,
            correo: s.correoElectronico,
            activo: s.activo !== false,
            // Campos descriptivos del backend
            // El backend puede enviar como objeto {valor} o como string directo
            tipoEstablecimiento: tipoId ? String(tipoId).padStart(2, '0') : '02',
            tipoEstablecimientoDesc: tipoDesc,
            departamentoDesc: s.departamento?.valor || s.departamentoNombre || '',
            municipioDesc: s.municipio?.valor || s.municipioNombre || '',
            catTipoEstablecimientoId: tipoId || null,
            catDepartamentoId: s.catDepartamentoId,
            catMunicipioId: s.catMunicipioId
          }
        })

      // Guardar en ambas listas
      commit('SET_ESTABLECIMIENTOS_USUARIO', establecimientos)
      commit('SET_ESTABLECIMIENTOS', establecimientos)

      // Intentar restaurar el establecimiento guardado
      const savedEstablecimientoId = localStorage.getItem('dte_establecimiento_id')
      let establecimientoSeleccionado = null

      if (savedEstablecimientoId) {
        establecimientoSeleccionado = establecimientos.find(e => e.id === parseInt(savedEstablecimientoId))
      }

      // Si no se encontró el guardado (o no había ninguno), seleccionar automáticamente
      if (!establecimientoSeleccionado && establecimientos.length > 0) {
        // Buscar primero la Casa Matriz (tipo '01' o catTipoEstablecimientoId === 1)
        establecimientoSeleccionado = establecimientos.find(
          e => e.tipoEstablecimiento === '01' || e.catTipoEstablecimientoId === 1
        ) || establecimientos[0]
      }

      if (establecimientoSeleccionado) {
        commit('SET_ESTABLECIMIENTO_ACTUAL', establecimientoSeleccionado)
        localStorage.setItem('dte_establecimiento_id', establecimientoSeleccionado.id)
      }
    } catch (error) {
      console.error('Error al cargar establecimientos del usuario:', error)
      commit('SET_ERROR', 'No se pudieron cargar las sucursales.')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: selectEmisor
   * ===========================================================================
   *
   * Selecciona un emisor y lo guarda en localStorage.
   *
   * IMPORTANTE:
   * Si el establecimiento actual no pertenece al nuevo emisor,
   * lo limpiamos para evitar inconsistencias.
   * También cargamos las sucursales del nuevo emisor.
   *
   * @param {Object} context - { commit, state, dispatch, rootState }
   * @param {number} emisorId - ID del emisor a seleccionar
   */
  async selectEmisor({ commit, state, dispatch, rootState }, emisorId) {
    // Buscar el emisor en la lista
    const emisor = state.emisores.find(e => e.id === emisorId)
    if (emisor) {
      commit('SET_EMISOR_ACTUAL', emisor)
      localStorage.setItem('dte_emisor_id', emisorId)

      // Limpiar establecimiento actual al cambiar de emisor
      commit('SET_ESTABLECIMIENTO_ACTUAL', null)
      localStorage.removeItem('dte_establecimiento_id')

      // Si es SuperAdmin, cargar las sucursales del emisor seleccionado
      const userRol = rootState.auth?.user?.rol
      if (userRol === 'SuperAdmin') {
        await dispatch('fetchEstablecimientosByEmisor', emisorId)
      }
    }
  },

  /**
   * ===========================================================================
   * ACTION: fetchEstablecimientosByEmisor
   * ===========================================================================
   *
   * Carga los establecimientos de un emisor específico.
   * Este método es para SuperAdmin cuando selecciona un emisor diferente.
   *
   * IMPORTANTE: Este método usa cambiarContexto internamente, lo que:
   * 1. Genera un nuevo token JWT con el emisorId en el contexto
   * 2. Actualiza el token en localStorage
   * 3. Permite que /api/sucursales devuelva las sucursales del emisor
   *
   * @param {Object} context - { commit, rootState }
   * @param {number} emisorId - ID del emisor
   */
  async fetchEstablecimientosByEmisor({ commit, rootState }, emisorId) {
    commit('SET_LOADING', true)
    try {
      // Usar el endpoint específico para obtener sucursales por emisor
      // NOTA: getByEmisorId internamente usa cambiarContexto para cambiar el token
      const respuesta = await sucursalesService.getByEmisorId(emisorId)

      // Sincronizar el nuevo token con el store de auth
      // El token ya fue actualizado en localStorage por el servicio
      const newToken = localStorage.getItem('dte_token')
      if (newToken && rootState.auth) {
        commit('auth/SET_TOKEN', newToken, { root: true })
      }

      // Asegurar que sucursalesBasicas sea un array
      const sucursalesBasicas = Array.isArray(respuesta) ? respuesta : (respuesta?.items || [])
      
      // Si no hay sucursales, terminar
      if (!sucursalesBasicas || sucursalesBasicas.length === 0) {
                commit('SET_ESTABLECIMIENTOS', [])
        commit('SET_ESTABLECIMIENTOS_USUARIO', [])
        commit('SET_LOADING', false)
        return
      }

      // Obtener detalles completos en paralelo
      const detallesPromises = sucursalesBasicas.map(s =>
        sucursalesService.getById(s.id).catch(err => {
          console.warn(`No se pudo cargar detalle de sucursal ${s.id}:`, err)
          return null
        })
      )
      const detallesCompletos = await Promise.all(detallesPromises)

      // Obtener catálogo de tipos de establecimiento para buscar descripciones
      const tiposEstablecimiento = rootState.catalogos?.tiposEstablecimiento || []

      // Mapear los datos completos al formato del frontend
      const establecimientos = detallesCompletos
        .filter(s => s !== null)
        .map(s => {
          // Buscar la descripción del tipo de establecimiento en el catálogo
          const tipoId = s.catTipoEstablecimientoId
          const tipoCatalogo = tiposEstablecimiento.find(t => t.id === tipoId)
          const tipoDesc = s.tipoEstablecimiento?.valor || s.tipoEstablecimientoNombre || tipoCatalogo?.valor || ''

          return {
            id: s.id,
            // Usar el emisorId del parámetro para garantizar que siempre esté presente
            // (el endpoint getById puede no devolver este campo)
            emisorId: s.emisorId || emisorId,
            nombre: s.nombre,
            codigo: s.codigo || s.codigoEstablecimiento,
            codigoPuntoVenta: s.codigoPuntoVenta,
            codigoPuntoVentaMH: s.codigoPuntoVentaMH,
            codigoEstablecimientoMH: s.codigoEstablecimiento,
            codigoMH: s.codigoMH,
            direccion: s.direccion,
            telefono: s.telefono,
            correo: s.correoElectronico,
            activo: s.activo !== false,
            // El backend puede enviar como objeto {valor} o como string directo
            tipoEstablecimiento: tipoId ? String(tipoId).padStart(2, '0') : '02',
            tipoEstablecimientoDesc: tipoDesc,
            departamentoDesc: s.departamento?.valor || s.departamentoNombre || '',
            municipioDesc: s.municipio?.valor || s.municipioNombre || '',
            catTipoEstablecimientoId: tipoId || null,
            catDepartamentoId: s.catDepartamentoId,
            catMunicipioId: s.catMunicipioId
          }
        })

      // Guardar en ambas listas
            commit('SET_ESTABLECIMIENTOS', establecimientos)
      commit('SET_ESTABLECIMIENTOS_USUARIO', establecimientos)

      // Autoseleccionar la sucursal principal (Casa Matriz) o la primera disponible
      if (establecimientos.length > 0) {
        const establecimientoSeleccionado = establecimientos.find(
          e => e.tipoEstablecimiento === '01' || e.catTipoEstablecimientoId === 1
        ) || establecimientos[0]
        commit('SET_ESTABLECIMIENTO_ACTUAL', establecimientoSeleccionado)
        localStorage.setItem('dte_establecimiento_id', establecimientoSeleccionado.id)
      }
          } catch (error) {
      console.error('=== ERROR fetchEstablecimientosByEmisor ===')
      console.error('Error al cargar establecimientos del emisor:', error)
      commit('SET_ERROR', 'No se pudieron cargar las sucursales del emisor.')
      // Si falla, limpiar las listas
      commit('SET_ESTABLECIMIENTOS', [])
      commit('SET_ESTABLECIMIENTOS_USUARIO', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: selectEstablecimiento
   * ===========================================================================
   *
   * Selecciona un establecimiento y lo guarda en localStorage.
   *
   * @param {Object} context - { commit, state }
   * @param {number} establecimientoId - ID del establecimiento a seleccionar
   */
  selectEstablecimiento({ commit, state }, establecimientoId) {
    // Buscar primero en establecimientos del usuario
    let establecimiento = state.establecimientosUsuario.find(e => e.id === establecimientoId)

    // Si no está en los del usuario, buscar en todos
    if (!establecimiento) {
      establecimiento = state.establecimientos.find(e => e.id === establecimientoId)
    }

    if (establecimiento) {
      commit('SET_ESTABLECIMIENTO_ACTUAL', establecimiento)
      localStorage.setItem('dte_establecimiento_id', establecimientoId)
    }
  },

  /**
   * ===========================================================================
   * ACTION: clearEmisor
   * ===========================================================================
   *
   * Limpia el emisor y establecimiento seleccionados.
   * Se usa cuando se quiere resetear la selección.
   *
   * @param {Object} context - { commit }
   */
  clearEmisor({ commit }) {
    commit('SET_EMISOR_ACTUAL', null)
    commit('SET_ESTABLECIMIENTO_ACTUAL', null)
    localStorage.removeItem('dte_emisor_id')
    localStorage.removeItem('dte_establecimiento_id')
  },

  /**
   * ===========================================================================
   * ACTION: clearEstablecimiento
   * ===========================================================================
   *
   * Limpia solo el establecimiento seleccionado.
   *
   * @param {Object} context - { commit }
   */
  clearEstablecimiento({ commit }) {
    commit('SET_ESTABLECIMIENTO_ACTUAL', null)
    localStorage.removeItem('dte_establecimiento_id')
  },

  /**
   * ===========================================================================
   * ACTION: clearAll
   * ===========================================================================
   *
   * Limpia toda la información del módulo.
   * Se llama al cerrar sesión.
   *
   * @param {Object} context - { commit }
   */
  clearAll({ commit }) {
    commit('CLEAR_ALL')
    localStorage.removeItem('dte_emisor_id')
    localStorage.removeItem('dte_establecimiento_id')
  },

  // =================================================================================
  // CRUD EMISORES
  // =================================================================================

  /**
   * Crea un nuevo emisor
   * @param {Object} context - { commit, dispatch }
   * @param {Object} emisorData - Datos del nuevo emisor
   */
  async createEmisor({ commit, dispatch }, emisorData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await emisoresService.create(emisorData)
      // Recargar la lista después de crear
      await dispatch('fetchEmisores')
    } catch (error) {
      console.error('Error al crear emisor:', error)
      commit('SET_ERROR', 'No se pudo crear el emisor.')
      throw error  // Re-lanzar para que el componente pueda manejarlo
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Actualiza el emisor usando el endpoint PUT /api/emisores/{id}
   * Usa UpdateEmisorDto que tiene todos los campos
   *
   * @param {Object} context - { commit, dispatch }
   * @param {Object} payload - { id, data }
   */
  async updateEmisor({ commit, dispatch }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      // Mapear al formato que espera el backend (UpdateEmisorDto)
      const updateData = {
        nit: data.nit,
        nrc: data.nrc,
        nombreRazonSocial: data.nombreRazonSocial,
        nombreComercial: data.nombreComercial || null,
        codigoActividad: data.codigoActividad,
        descripcionActividad: data.descripcionActividad,
        catTipoEstablecimientoId: data.catTipoEstablecimientoId,
        correoElectronico: data.correoElectronico,
        telefono: data.telefono || '',
        catDepartamentoId: data.catDepartamentoId,
        catMunicipioId: data.catMunicipioId,
        direccion: data.direccion
      }
      await emisoresService.update(id, updateData)
      await dispatch('fetchEmisores')
    } catch (error) {
      console.error('Error al actualizar emisor:', error)
      commit('SET_ERROR', 'No se pudo actualizar el emisor.')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Actualiza el perfil del emisor actual usando PUT /api/emisores/mi-perfil
   * Usa UpdatePerfilEmisorDto que incluye los códigos MH
   * Solo para EmisorAdmin
   *
   * @param {Object} context - { commit, dispatch }
   * @param {Object} data - Datos a actualizar
   */
  async updateMiPerfil({ commit, dispatch }, data) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      // Mapear al formato que espera el backend (UpdatePerfilEmisorDto)
      const updateData = {
        nombreComercial: data.nombreComercial || null,
        correoElectronico: data.correoElectronico,
        telefono: data.telefono || '',
        direccion: data.direccion,
        nrc: data.nrc || null,
        codigoActividad: data.codigoActividad || null,
        descripcionActividad: data.descripcionActividad || null,
        catDepartamentoId: data.catDepartamentoId || null,
        catMunicipioId: data.catMunicipioId || null,
        catTipoEstablecimientoId: data.catTipoEstablecimientoId || null,
        // Códigos MH
        codEstableMH: data.codEstableMH || null,
        codEstable: data.codEstable || null,
        codPuntoVentaMH: data.codPuntoVentaMH || null,
        codPuntoVenta: data.codPuntoVenta || null,
        // Credenciales MH
        mhUsuario: data.mhUsuario || null,
        mhClaveApi: data.mhClaveApi || null,
        mhLlavePrivada: data.mhLlavePrivada || null,
        mhLlavePublica: data.mhLlavePublica || null,
        // Credenciales MH Producción
        mhUsuarioProd: data.mhUsuarioProd || null,
        mhClaveApiProd: data.mhClaveApiProd || null,
        mhLlavePrivadaProd: data.mhLlavePrivadaProd || null,
        mhLlavePublicaProd: data.mhLlavePublicaProd || null,
        // Configuración SMTP (SmtpUser y EmailRemitente usan el correoElectronico)
        smtpUser: data.smtpUser || data.correoElectronico || null,
        smtpPassword: data.smtpPassword || null,
        emailRemitente: data.smtpUser || data.correoElectronico || null
      }
      await emisoresService.updateMiPerfil(updateData)
      await dispatch('fetchEmisores')
    } catch (error) {
      console.error('Error al actualizar perfil del emisor:', error)
      commit('SET_ERROR', 'No se pudo actualizar el perfil del emisor.')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Elimina un emisor
   * @param {Object} context - { commit, dispatch }
   * @param {number} id - ID del emisor a eliminar
   */
  async deleteEmisor({ commit, dispatch }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await emisoresService.delete(id)
      await dispatch('fetchEmisores')
    } catch (error) {
      console.error('Error al eliminar emisor:', error)
      commit('SET_ERROR', 'No se pudo eliminar el emisor.')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // =================================================================================
  // CRUD ESTABLECIMIENTOS
  // =================================================================================
  // NOTA: Estas operaciones aún usan mock (manipulación local del state)
  // TODO: Reemplazar con llamadas al backend cuando esté listo

  /**
   * Crea un nuevo establecimiento
   *
   * Llama al backend para crear la sucursal y luego refresca la lista.
   *
   * @param {Object} context - { commit, dispatch }
   * @param {Object} establecimientoData - Datos del nuevo establecimiento
   */
  async createEstablecimiento({ commit, dispatch }, establecimientoData) {
    commit('SET_LOADING', true)
    try {
      // Llamar al servicio para crear en el backend
      const nuevoEstablecimiento = await sucursalesService.create(establecimientoData)
      // Refrescar la lista de establecimientos desde el backend
      await dispatch('fetchEstablecimientos')
      return nuevoEstablecimiento
    } catch (error) {
      console.error('Error al crear establecimiento:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Actualiza un establecimiento existente
   * @param {Object} context - { commit, dispatch }
   * @param {Object} payload - { id, data }
   */
  async updateEstablecimiento({ commit, dispatch }, { id, data }) {
    commit('SET_LOADING', true)
    try {
      // Llamar al servicio de sucursales
      await sucursalesService.update(id, data)
      // Refrescar la lista de establecimientos desde el backend
      await dispatch('fetchEstablecimientos')
    } catch (error) {
      console.error('Error al actualizar establecimiento:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Elimina un establecimiento (MOCK)
   * @param {Object} context - { commit, state }
   * @param {number} id - ID del establecimiento a eliminar
   */
  async deleteEstablecimiento({ commit, state }, id) {
    commit('SET_LOADING', true)
    try {
      // TODO: Reemplazar con llamada a API
      // filter crea un nuevo array sin el elemento eliminado
      const nuevosEstablecimientos = state.establecimientos.filter(e => e.id !== id)
      commit('SET_ESTABLECIMIENTOS', nuevosEstablecimientos)

      // Si era el actual, limpiarlo
      if (state.establecimientoActual?.id === id) {
        commit('SET_ESTABLECIMIENTO_ACTUAL', null)
        localStorage.removeItem('dte_establecimiento_id')
      }
    } catch (error) {
      console.error('Error al eliminar establecimiento:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL MÓDULO
 * =====================================================================================
 *
 * namespaced: true significa que las llamadas incluyen 'emisor/':
 * - store.getters['emisor/emisorActual']
 * - store.dispatch('emisor/fetchEmisores')
 *
 * FLUJO TÍPICO DE USO:
 * -------------------
 * 1. Al iniciar sesión, cargar emisores y establecimientos:
 *    await store.dispatch('emisor/fetchEmisores')
 *    await store.dispatch('emisor/fetchEstablecimientosUsuario')
 *
 * 2. Mostrar selector en el header:
 *    const emisor = computed(() => store.getters['emisor/emisorActual'])
 *    const establecimiento = computed(() => store.getters['emisor/establecimientoActual'])
 *
 * 3. Al emitir factura, obtener datos:
 *    const datosEmision = store.getters['emisor/datosEmision']
 *    if (!datosEmision) {
 *      alert('Seleccione emisor y establecimiento')
 *      return
 *    }
 *
 * 4. Al cerrar sesión:
 *    store.dispatch('emisor/clearAll')
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
